/**
 * Klaviyo Back in Stock
 *
 * Posts signups to Klaviyo's public Client API endpoint:
 *   POST https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id={publicKey}
 *
 * Klaviyo's built-in Back in Stock subscription automatically removes a
 * subscriber from the notification list once their variant is notified,
 * so no extra cleanup is needed on our end. To re-subscribe, the customer
 * just clicks "Notify Me" again.
 *
 * Docs: https://developers.klaviyo.com/en/reference/create_back_in_stock_subscription
 */
(function () {
  'use strict';

  var KLAVIYO_ENDPOINT = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/';
  var KLAVIYO_REVISION = '2024-10-15';

  function init(root) {
    var dialog = root.querySelector('dialog');
    var formEl = root.querySelector('[data-klaviyo-bis-form]');
    var variantInput = root.querySelector('[data-klaviyo-bis-variant-id]');
    var emailInput = root.querySelector('[data-klaviyo-bis-email]');
    var variantLabel = root.querySelector('[data-klaviyo-bis-variant-label]');
    var successEl = root.querySelector('[data-klaviyo-bis-success]');
    var errorEl = root.querySelector('[data-klaviyo-bis-error]');
    var closeButtons = root.querySelectorAll('[data-klaviyo-bis-close]');
    var submitBtn = root.querySelector('[data-klaviyo-bis-submit]');
    var headingEl = root.querySelector('[data-klaviyo-bis-heading]');
    var bodyEl = root.querySelector('[data-klaviyo-bis-body]');
    var companyId = root.dataset.companyId;
    var productId = root.dataset.productId;
    // Modal key — lets a button explicitly target one modal when multiple are
    // on the same page (e.g., a coming-soon PDP that also has a teaser block).
    // Defaults to the product id so existing call sites keep working unchanged.
    var modalKey = root.dataset.modalKey || productId;

    // Per-mode copy. The snippet ships defaults but the merchant can override
    // via theme settings; whatever Liquid rendered into data-* wins here.
    var copy = {
      standard: {
        heading: root.dataset.standardHeading || (headingEl && headingEl.textContent.trim()) || '',
        body: root.dataset.standardBody || (bodyEl && bodyEl.textContent.trim()) || ''
      },
      'coming-soon': {
        heading: root.dataset.comingSoonHeading || '',
        body: root.dataset.comingSoonBody || ''
      }
    };

    if (!dialog || !formEl || !submitBtn || !emailInput || !companyId) {
      if (window.console && console.warn) {
        console.warn('[Klaviyo BIS] init bailed — missing element', {
          dialog: !!dialog,
          formEl: !!formEl,
          submitBtn: !!submitBtn,
          emailInput: !!emailInput,
          companyId: !!companyId
        });
      }
      return;
    }

    // Tracks the submission mode for the currently-open dialog:
    //   'standard'    — genuine sold-out, posts to Klaviyo's back-in-stock-subscriptions endpoint.
    //                   Klaviyo's Shopify integration auto-fires the email when inventory comes back.
    //   'reserved'    — Phase 2 subscription-reserved variants, appends the variant id to a
    //                   watching_variants profile property (the Custom Reserve pattern).
    //   'coming-soon' — pre-launch products on the `coming-soon` template, or any teaser
    //                   block on another page. Writes a single fixed profile property
    //                   `coming_soon = true` so marketing can build one segment that catches
    //                   every signup regardless of which product/page they came from. We
    //                   deliberately bypass Klaviyo's back-in-stock-subscriptions endpoint
    //                   so marketing controls when the launch announcement fires.
    var currentMode = 'standard';

    function resetMessages() {
      if (successEl) successEl.hidden = true;
      if (errorEl) errorEl.hidden = true;
      formEl.classList.remove('klaviyo-bis__form--sent');
    }

    function openModal(detail) {
      resetMessages();
      emailInput.value = '';

      var requestedMode = detail && detail.mode;
      if (requestedMode === 'reserved' || requestedMode === 'coming-soon') {
        currentMode = requestedMode;
      } else {
        currentMode = 'standard';
      }

      // Swap modal copy based on mode. 'reserved' falls through to standard
      // copy because reserved variants are still framed as "back in stock".
      var copyKey = currentMode === 'coming-soon' ? 'coming-soon' : 'standard';
      var modeCopy = copy[copyKey];
      if (modeCopy) {
        if (headingEl && modeCopy.heading) headingEl.textContent = modeCopy.heading;
        if (bodyEl && modeCopy.body) bodyEl.textContent = modeCopy.body;
      }

      if (detail && detail.variantId) {
        variantInput.value = detail.variantId;
      }

      if (variantLabel) {
        var labelParts = [];
        if (detail && detail.productTitle) labelParts.push(detail.productTitle);
        // Skip Shopify's auto-generated "Default Title" when a product has no variants
        if (detail && detail.variantTitle && detail.variantTitle !== 'Default Title') {
          labelParts.push(detail.variantTitle);
        }
        variantLabel.textContent = labelParts.join(' — ');
      }

      if (typeof dialog.showModal === 'function') {
        dialog.showModal();
      } else {
        dialog.setAttribute('open', '');
      }
    }

    function closeModal() {
      if (typeof dialog.close === 'function') {
        dialog.close();
      } else {
        dialog.removeAttribute('open');
      }
    }

    // Listen for global open requests. Routing precedence:
    //   1. `target` — explicit modal-key match (used by teaser blocks and any
    //      future case where a button needs to address one specific modal).
    //   2. `productId` — product-scoped (existing buy-buttons + coming-soon-notify flow).
    //   3. Neither — open this modal anyway (legacy fallback).
    document.addEventListener('klaviyo-bis:open', function (e) {
      var detail = e.detail || {};
      if (detail.target) {
        if (String(detail.target) !== String(modalKey)) return;
      } else if (detail.productId) {
        if (String(detail.productId) !== String(productId)) return;
      }
      openModal(detail);
    });

    closeButtons.forEach(function (btn) {
      btn.addEventListener('click', closeModal);
    });

    // Close if clicking on the backdrop
    dialog.addEventListener('click', function (e) {
      if (e.target === dialog) {
        closeModal();
      }
    });

    // Submit on click of the inner button OR Enter key in the email field.
    submitBtn.addEventListener('click', handleSubmit);
    emailInput.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSubmit();
      }
    });

    function handleSubmit() {
      resetMessages();

      var email = emailInput.value.trim();
      var variantId = variantInput.value;

      // Lightweight email validation (since we removed the native form's required + type checks)
      var emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
      if (!emailValid) {
        if (errorEl) errorEl.hidden = false;
        return;
      }

      // Variant id is required for the standard/reserved flows (the request
      // payload is variant-scoped). Coming-soon writes a generic
      // `coming_soon = true` profile property, so no variant context needed.
      if (currentMode !== 'coming-soon' && !variantId) {
        if (errorEl) errorEl.hidden = false;
        return;
      }

      submitBtn.disabled = true;
      submitBtn.setAttribute('aria-busy', 'true');

      var submitPromise;
      if (currentMode === 'reserved') {
        submitPromise = submitReserved(email, variantId);
      } else if (currentMode === 'coming-soon') {
        submitPromise = submitComingSoon(email);
      } else {
        submitPromise = submitStandard(email, variantId);
      }

      submitPromise
        .then(function () {
          if (successEl) successEl.hidden = false;
          formEl.classList.add('klaviyo-bis__form--sent');
        })
        .catch(function (err) {
          if (window.console && console.error) {
            console.error('[Klaviyo BIS]', err);
          }
          if (errorEl) errorEl.hidden = false;
        })
        .finally(function () {
          if (submitBtn) {
            submitBtn.disabled = false;
            submitBtn.removeAttribute('aria-busy');
          }
        });
    }

    // Standard genuine-sold-out flow: POST to Klaviyo's native back-in-stock
    // subscription endpoint. Klaviyo's Shopify integration fires the email
    // automatically when inventory comes back.
    function submitStandard(email, variantId) {
      var payload = {
        data: {
          type: 'back-in-stock-subscription',
          attributes: {
            channels: ['EMAIL'],
            profile: {
              data: {
                type: 'profile',
                attributes: { email: email }
              }
            }
          },
          relationships: {
            variant: {
              data: {
                type: 'catalog-variant',
                // Klaviyo's Shopify integration stores variants as
                // $shopify:::$default:::{variantId}
                id: '$shopify:::$default:::' + variantId
              }
            }
          }
        }
      };

      return fetch(KLAVIYO_ENDPOINT + '?company_id=' + encodeURIComponent(companyId), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          revision: KLAVIYO_REVISION
        },
        body: JSON.stringify(payload)
      }).then(function (response) {
        if (response.status >= 200 && response.status < 300) return;
        return response.text().then(function (body) {
          // Treat "already subscribed" as success too
          if (body && body.toLowerCase().indexOf('already') !== -1) return;
          throw new Error('Klaviyo error ' + response.status + ': ' + body);
        });
      });
    }

    // Coming Soon flow: writes a single fixed profile property `coming_soon = true`.
    // Marketing builds one Klaviyo Segment ("coming_soon equals true") that
    // catches every signup across all coming-soon products and any teaser
    // blocks elsewhere on the site, then targets that segment from their
    // launch campaign. We bypass the back-in-stock-subscriptions endpoint on
    // purpose — marketing wants control of when the launch email fires, not
    // Klaviyo's BIS flow auto-firing on inventory transitions.
    //
    // To clear the property post-launch, marketing can bulk-update profiles
    // in Klaviyo (Profiles → filter by `coming_soon = true` → Bulk Edit →
    // unset), or build a flow that unsets it on launch-email send.
    function submitComingSoon(email) {
      var payload = {
        data: {
          type: 'profile',
          attributes: {
            email: email,
            properties: {
              coming_soon: true
            }
          }
        }
      };

      return fetch(
        'https://a.klaviyo.com/client/profiles/?company_id=' + encodeURIComponent(companyId),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            revision: KLAVIYO_REVISION
          },
          body: JSON.stringify(payload)
        }
      ).then(function (response) {
        if (response.status >= 200 && response.status < 300) return;
        return response.text().then(function (body) {
          throw new Error('Klaviyo error ' + response.status + ': ' + body);
        });
      });
    }

    // Phase 2 reserved-variant flow: append the variant_id to the customer's
    // watching_variants profile property using Klaviyo's `meta.patch_properties`
    // block, which is the documented mechanism for atomic array operations
    // (append, unappend, unset) on profile properties.
    //
    // We bypass _learnq.push for this because the legacy SDK only knows the
    // older `$append` operator inside `properties`, which the modern Klaviyo
    // /client/profiles/ endpoint silently stores as a literal property
    // instead of processing as an array operation. Calling /client/profiles/
    // directly with the meta.patch_properties.append structure works.
    //
    // Docs: https://www.klaviyo.com/blog/solution-recipe-25-append-unappend-and-unset-custom-properties-programmatically-with-klaviyo
    function submitReserved(email, variantId) {
      var payload = {
        data: {
          type: 'profile',
          attributes: {
            email: email,
            properties: {}
          },
          meta: {
            patch_properties: {
              append: {
                watching_variants: String(variantId)
              }
            }
          }
        }
      };

      return fetch(
        'https://a.klaviyo.com/client/profiles/?company_id=' + encodeURIComponent(companyId),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            revision: KLAVIYO_REVISION
          },
          body: JSON.stringify(payload)
        }
      ).then(function (response) {
        if (response.status >= 200 && response.status < 300) return;
        return response.text().then(function (body) {
          throw new Error('Klaviyo error ' + response.status + ': ' + body);
        });
      });
    }
  }

  function bootAll() {
    document.querySelectorAll('klaviyo-back-in-stock').forEach(function (el) {
      if (el.__klaviyoBisBound) return;
      el.__klaviyoBisBound = true;
      init(el);
    });
    bindNotifyButtons();
    bindVariantWatchers();
  }

  // Read variant availability for a given product id from the embedded JSON.
  function getVariantMap(productId) {
    var script = document.querySelector(
      'script[data-product-variants-availability="' + productId + '"]'
    );
    if (!script) return null;
    try {
      return JSON.parse(script.textContent);
    } catch (err) {
      return null;
    }
  }

  // Toggle the add-to-cart wrapper vs the notify-me button based on availability.
  function applyVariantState(form, variantId) {
    var notifyBtn = form.querySelector('[data-notify-me-button]');
    if (!notifyBtn) return;

    var atcWrapper = form.querySelector('[data-atc-wrapper]');
    var productId = notifyBtn.getAttribute('data-product-id');
    var variants = getVariantMap(productId);
    if (!variants) return;

    var variant = variants[String(variantId)];
    if (!variant) return;

    if (variant.available === false) {
      notifyBtn.setAttribute('data-variant-id', String(variantId));
      notifyBtn.setAttribute('data-variant-title', variant.title || '');
      // Subscription-reserved variants (see blocks/buy-buttons.liquid) take the
      // reserved submit path; a genuine sold-out variant goes back to standard.
      if (variant.reserved) {
        notifyBtn.setAttribute('data-bis-mode', 'reserved');
      } else if (notifyBtn.getAttribute('data-bis-mode') === 'reserved') {
        notifyBtn.removeAttribute('data-bis-mode');
      }
      notifyBtn.hidden = false;
      if (atcWrapper) atcWrapper.hidden = true;
    } else {
      notifyBtn.hidden = true;
      if (atcWrapper) atcWrapper.hidden = false;
    }
  }

  function bindNotifyButtons() {
    document.querySelectorAll('[data-notify-me-button]').forEach(function (btn) {
      if (btn.__klaviyoBisBound) return;
      btn.__klaviyoBisBound = true;

      btn.addEventListener('click', function () {
        var detail = {
          productId: btn.getAttribute('data-product-id'),
          productTitle: btn.getAttribute('data-product-title') || '',
          variantId: btn.getAttribute('data-variant-id'),
          variantTitle: btn.getAttribute('data-variant-title') || '',
          // Optional explicit modal target. When set, only the modal with a
          // matching data-modal-key opens (used by teaser blocks so multiple
          // modals on the same page don't all fire at once).
          target: btn.getAttribute('data-modal-target') || '',
          // Submission mode:
          //   'standard'    (default) → Klaviyo back-in-stock-subscriptions endpoint
          //   'reserved'              → append variant id to watching_variants property
          //   'coming-soon'           → set coming_soon = true on profile
          mode: btn.getAttribute('data-bis-mode') || 'standard'
        };
        document.dispatchEvent(new CustomEvent('klaviyo-bis:open', { detail: detail }));
      });
    });
  }

  function bindVariantWatchers() {
    // Each product form has an input[name="id"] that gets its value set
    // when the customer changes variants. Watch both attribute and value
    // changes so we catch all the theme's update paths.
    document.querySelectorAll('form[action*="/cart/add"], form[data-type="add-to-cart-form"]').forEach(function (form) {
      if (form.__klaviyoBisVariantBound) return;
      form.__klaviyoBisVariantBound = true;

      var variantInput = form.querySelector('input[name="id"]');
      if (!variantInput) return;

      // Sync on initial load (in case the form server-rendered for a sold-out variant)
      applyVariantState(form, variantInput.value);

      // Listen for input/change events (most pickers dispatch these)
      variantInput.addEventListener('change', function () {
        applyVariantState(form, variantInput.value);
      });
      variantInput.addEventListener('input', function () {
        applyVariantState(form, variantInput.value);
      });

      // Also watch the value attribute (some pickers set it via setAttribute)
      var observer = new MutationObserver(function () {
        applyVariantState(form, variantInput.value);
      });
      observer.observe(variantInput, { attributes: true, attributeFilter: ['value'] });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootAll);
  } else {
    bootAll();
  }

  // Re-bind if Shopify section rendering swaps markup
  document.addEventListener('shopify:section:load', bootAll);
})();
